import { useEffect, useState } from "react";
import type { HttpAgent } from "@ag-ui/client";
import type { SubagentEvent } from "~/components/assistant-v2/SubagentActivity";

/**
 * Subscribes to AG-UI CUSTOM events named "subagent" and accumulates
 * them by toolCallId. Returns events for a specific toolCallId.
 *
 * The backend emits these events when a subagent (e.g. python_analyzer)
 * starts or finishes a tool call, allowing the UI to show real-time
 * progress inside the parent tool card.
 */
export function useSubagentEvents(agent: HttpAgent): Map<string, SubagentEvent[]> {
  const [events, setEvents] = useState<Map<string, SubagentEvent[]>>(() => new Map());

  useEffect(() => {
    setEvents(new Map());

    const sub = agent.subscribe({
      onRunStartedEvent: () => {
        setEvents(new Map());
      },
      onCustomEvent: ({ event }: { event: unknown }) => {
        const e = event as { name?: string; value?: Record<string, unknown> };
        if (e.name !== "subagent") return;

        const v = e.value;
        if (!v?.tool_call_id || !v.kind || !v.tool_name) return;

        const evt: SubagentEvent = {
          subagent: (v.subagent as string) ?? "unknown",
          toolCallId: v.tool_call_id as string,
          kind: v.kind as SubagentEvent["kind"],
          toolName: v.tool_name as string,
          summary: v.summary as string | undefined,
        };

        setEvents((prev) => {
          const next = new Map(prev);
          const existing = next.get(evt.toolCallId) ?? [];
          next.set(evt.toolCallId, [...existing, evt]);
          return next;
        });
      },
    });

    return () => sub.unsubscribe();
  }, [agent]);

  return events;
}
